/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Component, ElementRef, EventEmitter, Inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { SelectGroup } from '@wfm/core';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'wfm-select-panel-new',
  templateUrl: './select-panel-new.component.html',
  styleUrls: [
    '../../../../../../core/src/lib/components/select-panel/select-panel.component.scss',
    './select-panel-new.component.scss'
  ]
})
export class SelectPanelNewComponent {
  @Input() items: Array<SelectGroup | any> = [];

  @Input() itemTemplate?: TemplateRef<any>;

  @Input() placeholder = '';

  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  @Input() labelField = '';

  @Input() iconField = '';

  @Input() maxSelection = 0;

  @Input() columns = 0;

  @Input() multiple = false;

  @Input() required = false;

  @Input() search = false;

  @Input() highlightSelected = false;

  @Input() grouping = false;

  @Input() selectAllOption = false;

  query = '';

  filteredItems: Array<SelectGroup | any>;

  @ViewChild('panelScroll') panelScroll: ElementRef;

  get selectedAll(): boolean {
    return !!(this.multiple && this.items && this.value?.length === this.items.length);
  }

  get isListOfObjects(): boolean {
    if (this.grouping) {
      return typeof (this.items[0] as SelectGroup).items[0] === 'object';
    }
    return typeof this.items[0] === 'object';
  }

  get itemStyle(): any {
    if (this.columns > 0 && !this.grouping) {
      return {
        'min-width': Math.round(100 / this.columns) + '%',
        float: 'left'
      };
    }

    return {};
  }

  get flatItems(): any[] {
    if (this.grouping) {
      return this.items.reduce((value, item) => (value as Array<any>).concat(item.items), []);
    }
    return this.items;
  }

  private window: Window;

  constructor(@Inject(DOCUMENT) private document) {
    this.window = this.document.defaultView;
  }

  itemClick(item: any) {
    const clickTimeout = this.window.setTimeout(() => {
      if (this.multiple) {
        if (!this.value) {
          this.value = [];
        }

        const index = this.value.indexOf(item);
        if (index > -1) {
          (this.value as any[]).splice(index, 1);
        } else {
          this.value.push(item);
        }
      } else {
        this.value = item;
      }

      this.updateFilteredItems();

      this.valueChange.emit(this.value);
      this.window.clearTimeout(clickTimeout);
    }, 0);
  }

  toggleSelectAll() {
    if (this.selectedAll) {
      this.value = [];
    } else {
      this.value = [...this.items];
    }

    this.updateFilteredItems();
    this.valueChange.emit(this.value);
  }

  updateFilteredItems() {
    let items = this.items || [];

    if (this.highlightSelected) {
      items = this.filterNotSelectedItems();
    }

    if (!this.search || !this.query) {
      this.filteredItems = items;
    } else {
      this.filteredItems = this.filterItems(items);
    }
  }

  scroll() {
    if (this.panelScroll && !this.highlightSelected) {
      const panel: HTMLElement = this.panelScroll.nativeElement;
      const firstSelected: HTMLElement = panel.querySelector('.select__option--selected');

      if (firstSelected && firstSelected.offsetTop > panel.offsetHeight) {
        panel.scroll(0, firstSelected.offsetTop);
      }
    }
  }

  private filterNotSelectedItems(): any[] {
    const value = this.value || [];

    if (this.grouping) {
      const items = [];

      for (const group of this.items) {
        items.push({
          label: (group as SelectGroup).label,
          items: (group as SelectGroup).items.filter(item => {
            return !value.find(v => v === item);
          })
        });
      }

      return items;
    }

    return this.items.filter(item => {
      return !value.find(v => v === item);
    });
  }

  private filterItems(items: any[]): any[] {
    if (this.grouping) {
      const groups = [];
      for (const group of items) {
        const filteredItems = group.items.filter(
          item =>
            (this.isListOfObjects ? item[this.labelField] : item).toLowerCase().indexOf(this.query.toLowerCase()) >= 0
        );
        if (filteredItems.length > 0) {
          groups.push({
            label: group.label,
            items: filteredItems
          });
        }
      }

      return groups;
    }

    return items.filter(
      item => (this.isListOfObjects ? item[this.labelField] : item).toLowerCase().indexOf(this.query.toLowerCase()) >= 0
    );
  }
}
